/**
 * File-transfer lifecycle logger (issue #808 item 7 — "aggressive logging for
 * debugging"). Emits ONE structured, greppable line per lifecycle transition so
 * a failed transfer is diagnosable from a single log read instead of the
 * webrtc-internals-dump + adb-logcat archaeology every P2P failure has required
 * during the hardware demo. The Kotlin side (`FtLog.kt`) emits the SAME event
 * vocabulary under Timber tag `FileTransferP2P`, so the sender (extension) log
 * and the receiver (phone) logcat correlate by (truncated) `transferId`.
 *
 * SECURITY (`.claude/rules/security.md`, `docs/file-transfer-architecture.md`):
 * this logger may carry ONLY non-sensitive scalars — an event name, the role,
 * a TRUNCATED transferId, COUNTS (chunks/bytes), state/trigger/outcome enum
 * labels, and elapsed ms. It must NEVER receive SDP, ICE candidates, IP
 * addresses, encryption keys, file names, file hashes, or any plaintext /
 * ciphertext bytes. `FtLogFields` is deliberately a CLOSED record of primitives
 * so that passing content is a compile error, not something a reviewer has to
 * catch. `transferId` is re-truncated here defensively regardless of caller.
 *
 * Off by default (`enabled = false`): each host opts in. The extension flips it
 * on behind the build-time `__FT_DEBUG__` define (on in dev/demo builds, off in
 * production) — mirroring the `__SENTRY_DSN__` gating pattern. When disabled,
 * `ftLog` is a cheap synchronous no-op.
 */

export type FtLogRole = "sender" | "receiver";

/** The closed set of lifecycle transitions. Mirrored verbatim by `FtLog.kt`'s `Event`. */
export type FtLogEvent =
  | "attempt_start"
  | "connect_result"
  | "signal_send"
  | "ice_state"
  | "dc_open"
  | "dc_close"
  | "transferring"
  | "chunk"
  | "ack"
  | "demote"
  | "complete"
  | "not_attempted"
  | "terminal"
  | "relay_fallback"
  /** Messages that arrived before a handler was wired, replayed instead of dropped. */
  | "dc_buffer_replay"
  /** Pre-handler buffer grew past its sanity bound — warn-only, nothing is ever dropped. */
  | "dc_buffer_large";

/**
 * Closed record of NON-SENSITIVE scalars only (see the security note above).
 * Every field is optional; callers pass only what the event carries.
 */
export interface FtLogFields {
  /** Truncated by `ftLog` to the first 8 chars regardless of what is passed. */
  transferId?: string;
  role?: FtLogRole;
  // --- counts (never content) ---
  sent?: number;
  acked?: number;
  received?: number;
  total?: number;
  bytes?: number;
  /** dc_buffer_replay: how many pre-handler messages were replayed instead of dropped. */
  buffered?: number;
  // --- classifiers (enum-ish labels, never free-form content) ---
  /** ice_state: checking | connected | disconnected | failed | closed. */
  state?: string;
  /** demote: T2 | T3 | T4 | T5 | T6 | early | stall | protocol | local_error. */
  trigger?: string;
  /** dc_close / not_attempted / relay_fallback: a short reason CLASS. */
  reason?: string;
  /** signal_send: ok | canceled | timeout. connect_result: channel | null | early_demote. */
  result?: string;
  /** A wire `FtErrorCode` (already an enum label, never content). */
  code?: string;
  /** terminal: completed | demoted | paused | cancelled | failed | stalled (#821 receiver liveness give-up). */
  outcome?: string;
  /** ms since the attempt started. */
  elapsedMs?: number;
}

/** Where a formatted line is written. Default writes to `console.info`. */
export type FtLogSink = (line: string, event: FtLogEvent, fields: FtLogFields) => void;

let enabled = false;
// Default sink writes to the console — this IS the debug logger, and `noConsole`
// is off for `@reflecto/client-core` (only `apps/mcp/**` enforces it). Hosts can
// override the sink via `configureFtLog` (e.g. to route into an in-app buffer).
let sink: FtLogSink = (line) => console.info(line);

/** Host opt-in. The extension calls this once at each context's entry with `enabled: __FT_DEBUG__`. */
export function configureFtLog(opts: { enabled?: boolean; sink?: FtLogSink }): void {
  if (opts.enabled !== undefined) enabled = opts.enabled;
  if (opts.sink !== undefined) sink = opts.sink;
}

export function ftLogEnabled(): boolean {
  return enabled;
}

/**
 * Emit one lifecycle line. No-op (and cheap) when disabled. Never throws into
 * the transfer path — a sink error is swallowed. Output shape is stable and
 * greppable: `ft <event> k=v k=v ...` (transferId first when present).
 */
export function ftLog(event: FtLogEvent, fields: FtLogFields = {}): void {
  if (!enabled) return;
  const safe: FtLogFields =
    fields.transferId !== undefined
      ? { ...fields, transferId: fields.transferId.slice(0, 8) }
      : fields;
  const parts = [`ft ${event}`];
  if (safe.transferId !== undefined) parts.push(`transferId=${safe.transferId}`);
  for (const [key, value] of Object.entries(safe)) {
    if (key === "transferId" || value === undefined) continue;
    parts.push(`${key}=${value}`);
  }
  try {
    sink(parts.join(" "), event, safe);
  } catch {
    // Logging must never disturb the transfer.
  }
}
